import { motion } from "framer-motion";
import { Landmark, Plane, Building2, Factory, Code2 } from "lucide-react";
import { useLang } from "@/lib/i18n";

const META = [
  { num: "01", icon: Landmark, img: "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?auto=format&fit=crop&w=1200&q=80", alt: "Financial district architecture", span: 7 },
  { num: "02", icon: Plane, img: "https://images.unsplash.com/photo-1645418245061-6490355fc74c?auto=format&fit=crop&w=1200&q=80", alt: "Aviation turbine", span: 5 },
  { num: "03", icon: Building2, img: null, alt: null, span: 7 },
  { num: "04", icon: Factory, img: null, alt: null, span: 5 },
  { num: "05", icon: Code2, img: null, alt: null, span: 12 },
];

export default function Chapters() {
  const { t } = useLang();
  const CHAPTERS = t("chapters.items").map((c, i) => ({ ...META[i], ...c }));
  return (
    <section id="experience" data-testid="experience-section" className="py-24 sm:py-32 bg-[#F4F4F1] border-y border-black">
      <div className="max-w-7xl mx-auto px-6">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-4">{t("chapters.overline")}</p>
        <h2 className="font-heading font-bold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mb-16">
          {t("chapters.h1a")}<br />{t("chapters.h1b")}<span className="text-accent">.</span>
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.num}
              data-testid={`chapter-${c.num}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className={`relative ${c.span === 12 ? "lg:col-span-12" : c.span === 7 ? "lg:col-span-7" : "lg:col-span-5"} border border-black bg-white p-8 sm:p-10 overflow-hidden group`}
            >
              <span className="absolute -top-4 right-4 font-mono font-bold text-[7rem] leading-none text-black/[0.06] select-none pointer-events-none" aria-hidden="true">
                {c.num}
              </span>
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <c.icon className="w-5 h-5 text-accent" strokeWidth={1.75} />
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent">{c.role}</span>
                </div>
                <h3 className="font-heading font-extrabold tracking-tight text-2xl sm:text-3xl mb-4">{c.org}</h3>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-xl">{c.body}</p>
                {c.img && (
                  <div className="mt-8 overflow-hidden border border-black">
                    <img
                      src={c.img}
                      alt={c.alt}
                      loading="lazy"
                      className="w-full h-48 sm:h-56 object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.03] transition-[filter,transform] duration-700"
                    />
                  </div>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
